import React, { useState, useEffect } from 'react';
import CalcShell from '../CalcShell';
import { CNetResult, CNetHintBar, G } from '../other/_cnet';

/* --------- Line plot on axes --------- */
function LinePlot({ x1, y1, x2, y2 }) {
  const w = 340, h = 220, pad = 30;
  const xs = [x1, x2, 0], ys = [y1, y2, 0];
  const minX = Math.min(...xs) - 1, maxX = Math.max(...xs) + 1;
  const minY = Math.min(...ys) - 1, maxY = Math.max(...ys) + 1;
  const tx = x => pad + ((x - minX) / (maxX - minX)) * (w - 2 * pad);
  const ty = y => h - pad - ((y - minY) / (maxY - minY)) * (h - 2 * pad);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full max-w-[380px]">
      <line x1={pad} y1={ty(0)} x2={w - pad} y2={ty(0)} stroke="#0f172a" markerEnd="url(#arrx)" />
      <line x1={tx(0)} y1={h - pad} x2={tx(0)} y2={pad} stroke="#0f172a" markerEnd="url(#arry)" />
      <defs>
        <marker id="arrx" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="#0f172a" /></marker>
        <marker id="arry" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="#0f172a" /></marker>
      </defs>
      <text x={w - pad + 4} y={ty(0) + 4} fontSize="11" fill="#0f172a">x</text>
      <text x={tx(0) - 8} y={pad - 6} fontSize="11" fill="#0f172a">y</text>
      <line x1={tx(x1)} y1={ty(y1)} x2={tx(x2)} y2={ty(y2)} stroke="#c1272d" strokeWidth="3" />
      <circle cx={tx(x1)} cy={ty(y1)} r="3" fill="#0f172a" />
      <circle cx={tx(x2)} cy={ty(y2)} r="3" fill="#0f172a" />
      <text x={tx(x1) + 6} y={ty(y1) - 6} fontSize="11">({x1}, {y1})</text>
      <text x={tx(x2) + 6} y={ty(y2) - 6} fontSize="11">({G(x2, 2)}, {G(y2, 2)})</text>
    </svg>
  );
}

/* -------- Section 1: 2 Points Known -------- */
function TwoPointsSection() {
  const [x1, sX1] = useState(1), [y1, sY1] = useState(1);
  const [x2, sX2] = useState(2), [y2, sY2] = useState(2);
  const [res, setRes] = useState(null);
  const calc = () => {
    const dx = Number(x2) - Number(x1);
    const dy = Number(y2) - Number(y1);
    if (dx === 0) { setRes({ error: 'Δx = 0: slope is undefined (vertical line).' }); return; }
    const m = dy / dx;
    const theta = Math.atan2(dy, dx) * 180 / Math.PI;
    const b = Number(y1) - m * Number(x1);
    const dist = Math.sqrt(dx * dx + dy * dy);
    setRes({ dx, dy, m, theta, b, dist, x1: +x1, y1: +y1, x2: +x2, y2: +y2 });
  };
  const clear = () => { sX1(1); sY1(1); sX2(2); sY2(2); setRes(null); };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [x1, y1, x2, y2]);
  return (
    <section>
      <h3 className="cnet-section-title">If the 2 Points are Known</h3>
      <div className="cnet-panel">
        <div className="grid grid-cols-4 gap-3">
          <div><div className="text-center font-semibold mb-1">X<sub>1</sub></div><input data-testid="slope-x1" className="cnet-input w-full" type="number" value={x1} onChange={e => sX1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Y<sub>1</sub></div><input data-testid="slope-y1" className="cnet-input w-full" type="number" value={y1} onChange={e => sY1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">X<sub>2</sub></div><input data-testid="slope-x2" className="cnet-input w-full" type="number" value={x2} onChange={e => sX2(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Y<sub>2</sub></div><input data-testid="slope-y2" className="cnet-input w-full" type="number" value={y2} onChange={e => sY2(e.target.value)} /></div>
        </div>
      </div>
      {res && res.error && <div className="text-red-600 mt-3">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-slate-900 leading-8" data-testid="slope-2pt-result">
            Slope (m) = <span className="font-mono">ΔY / ΔX</span> = <span className="font-mono">{res.dy} / {res.dx}</span> = <b className="text-[#4c7b25]">{G(res.m, 6)}</b>
          </div>
          <div className="text-slate-900 leading-8">
            θ = arctan(ΔY / ΔX) = <b>{G(res.theta, 6)}°</b> = <b>{G(res.theta / 180, 6)}π</b> rad
          </div>
          <div className="text-slate-900 mt-1">ΔX = {res.x2} − {res.x1} = <b>{G(res.dx, 4)}</b></div>
          <div className="text-slate-900">ΔY = {res.y2} − {res.y1} = <b>{G(res.dy, 4)}</b></div>
          <div className="text-slate-900">Distance (d) = √(ΔX² + ΔY²) = √({G(res.dx * res.dx, 4)} + {G(res.dy * res.dy, 4)}) = <b>{G(res.dist, 10)}</b></div>
          <div className="mt-3 font-bold">Equation of the line:</div>
          <div className="font-mono">y = {G(res.m, 4)}x + {G(res.b, 4)}</div>
          <div>When x = 0, y = <b>{G(res.b, 6)}</b></div>
          <div>When y = 0, x = <b>{res.m !== 0 ? G(-res.b / res.m, 6) : '—'}</b></div>
          <div className="mt-3 flex justify-center"><LinePlot x1={res.x1} y1={res.y1} x2={res.x2} y2={res.y2} /></div>
        </CNetResult>
      )}
    </section>
  );
}

/* -------- Section 2: 1 Point + Slope Known -------- */
function PointSlopeSection() {
  const [x1, sX1] = useState(1), [y1, sY1] = useState(1);
  const [d, sD] = useState(5);
  const [slope, sSlope] = useState(0.75);
  const [angle, sAngle] = useState('');
  const [res, setRes] = useState(null);
  const calc = () => {
    let m, theta;
    if (angle !== '' && !isNaN(+angle)) { theta = +angle; m = Math.tan(theta * Math.PI / 180); }
    else { m = +slope; theta = Math.atan(m) * 180 / Math.PI; }
    const rad = theta * Math.PI / 180;
    const x2a = +x1 + d * Math.cos(rad);
    const y2a = +y1 + d * Math.sin(rad);
    const x2b = +x1 - d * Math.cos(rad);
    const y2b = +y1 - d * Math.sin(rad);
    const b = +y1 - m * +x1;
    setRes({ m, theta, x1: +x1, y1: +y1, d: +d, x2a, y2a, x2b, y2b, dxA: x2a - +x1, dyA: y2a - +y1, dxB: x2b - +x1, dyB: y2b - +y1, thetaOr: (theta + 180) % 360, b });
  };
  const clear = () => { sX1(1); sY1(1); sD(5); sSlope(0.75); sAngle(''); setRes(null); };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [x1, y1, d, slope, angle]);
  return (
    <section className="mt-4">
      <h3 className="cnet-section-title">If 1 Point and the Slope are Known</h3>
      <div className="cnet-panel">
        <div className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-2 items-center max-w-md">
          <div className="text-right">X<sub>1</sub> =</div><input className="cnet-input" type="number" value={x1} onChange={e => sX1(e.target.value)} data-testid="slope-p-x1" />
          <div className="text-right">Y<sub>1</sub> =</div><input className="cnet-input" type="number" value={y1} onChange={e => sY1(e.target.value)} data-testid="slope-p-y1" />
          <div className="text-right">distance (d) =</div><input className="cnet-input" type="number" value={d} onChange={e => sD(e.target.value)} data-testid="slope-p-d" />
          <div className="text-right">slope (m) =</div><input className="cnet-input" type="number" value={slope} onChange={e => { sSlope(e.target.value); sAngle(''); }} data-testid="slope-p-m" />
        </div>
        <div className="flex items-center gap-2 mt-2 max-w-lg">
          <span className="font-bold">OR</span>
          <span>angle of incline (θ) =</span>
          <input className="cnet-input" type="number" value={angle} onChange={e => sAngle(e.target.value)} data-testid="slope-p-theta" />
          <span>°</span>
        </div>
      </div>
      {res && (
        <CNetResult>
          <div className="text-slate-900 leading-8" data-testid="slope-ptslope-result">
            X<sub>2</sub> = <b>{G(res.x2a, 4)}</b><br/>
            Y<sub>2</sub> = <b>{G(res.y2a, 4)}</b><br/>
            ΔX = <b>{G(res.dxA, 4)}</b><br/>
            ΔY = <b>{G(res.dyA, 4)}</b><br/>
            θ = <b>{G(res.theta, 10)}°</b>
          </div>
          <div className="mt-3 font-bold">Equation of the line:</div>
          <div className="font-mono">y = {G(res.m, 4)}x + {G(res.b, 4)}</div>
          <div>When x = 0, y = <b>{G(res.b, 6)}</b></div>
          <div>When y = 0, x = <b>{res.m !== 0 ? G(-res.b / res.m, 6) : '—'}</b></div>
          <div className="mt-3 flex justify-center"><LinePlot x1={res.x1} y1={res.y1} x2={res.x2a} y2={res.y2a} /></div>
          <div className="mt-4 font-bold text-lg">OR</div>
          <div className="text-slate-900 leading-8">
            X<sub>2</sub> = <b>{G(res.x2b, 4)}</b><br/>
            Y<sub>2</sub> = <b>{G(res.y2b, 4)}</b><br/>
            ΔX = <b>{G(res.dxB, 4)}</b><br/>
            ΔY = <b>{G(res.dyB, 4)}</b><br/>
            θ = <b>{G(res.thetaOr, 10)}°</b>
          </div>
          <div className="mt-3 font-bold">Equation of the line:</div>
          <div className="font-mono">y = {G(res.m, 4)}x + {G(res.b, 4)}</div>
          <div>When x = 0, y = <b>{G(res.b, 6)}</b></div>
          <div>When y = 0, x = <b>{res.m !== 0 ? G(-res.b / res.m, 6) : '—'}</b></div>
          <div className="mt-3 flex justify-center"><LinePlot x1={res.x1} y1={res.y1} x2={res.x2b} y2={res.y2b} /></div>
        </CNetResult>
      )}
    </section>
  );
}

export default function SlopeCalc() {
  return (
    <CalcShell showActions={false} look="cnet" title="Slope Calculator" subtitle="By definition, the slope or gradient of a line describes its steepness, incline, or grade.">
      <div className="grid lg:grid-cols-[1fr_260px] gap-6 mb-4">
        <div>
          <div className="font-mono text-slate-900 text-center text-lg my-2">
            m = <span className="inline-flex flex-col align-middle"><span>y₂ − y₁</span><span className="border-t border-current" /><span>x₂ − x₁</span></span> = tan(θ)
          </div>
          <div className="text-sm text-slate-900 mt-2"><b>Where</b></div>
          <div className="text-sm text-slate-900 ml-4">m — slope<br/>θ — angle of incline</div>
        </div>
        <svg viewBox="0 0 280 200" className="w-full max-w-[260px] mx-auto">
          <line x1="30" y1="170" x2="270" y2="170" stroke="#0f172a" />
          <line x1="30" y1="10" x2="30" y2="180" stroke="#0f172a" />
          <line x1="50" y1="150" x2="220" y2="50" stroke="#c1272d" strokeWidth="3" />
          <line x1="50" y1="150" x2="220" y2="150" stroke="#94a3b8" strokeDasharray="3 3" />
          <line x1="220" y1="150" x2="220" y2="50" stroke="#94a3b8" strokeDasharray="3 3" />
          <circle cx="50" cy="150" r="4" fill="#0f172a" />
          <circle cx="220" cy="50" r="4" fill="#0f172a" />
          <text x="10" y="160" fontSize="11">(x₁, y₁)</text>
          <text x="222" y="45" fontSize="11">(x₂, y₂)</text>
          <text x="230" y="100" fontSize="12" fontStyle="italic" fill="#c1272d">Δy</text>
          <text x="120" y="185" fontSize="11" fontStyle="italic" fill="#94a3b8">Δx</text>
          <text x="150" y="90" fontSize="11" fontStyle="italic" fill="#c1272d">d</text>
          <path d="M65,150 A15,15 0 0 1 68,140" fill="none" stroke="#4c7b25" strokeWidth="1.5" />
          <text x="72" y="148" fontSize="11" fill="#4c7b25">θ</text>
        </svg>
      </div>
      <CNetHintBar />
      <TwoPointsSection />
      <PointSlopeSection />
    </CalcShell>
  );
}
